import { FC } from "react";
import { CircularProgress, css, styled, Chip } from "@mui/material";
import { IconExpandMore } from "@/assets/icons";
import { Select } from "@/components/elements/Select/Select";
import { labelColor, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { PROCEDURE_STATUS } from "@/api/services/main/exam-steps/dtos/get-exam-step-learners.dto";

const SELECT_WIDTH = 168;
const MENU_WIDTH = 200;

const iconStyle = { position: "absolute", right: 8, pointerEvents: "none", flexShrink: 0 } as const;

const getStatusBg = (value: string): string => {
  const isInProgress = value.startsWith("NEXT:") || value === PROCEDURE_STATUS.IN_PROGRESS;
  if (value === PROCEDURE_STATUS.NEW_REGISTRATION) return labelColor.warning;
  if (value === PROCEDURE_STATUS.COMPLETED) return labelColor.success;
  if (isInProgress) return labelColor.progress;
  return labelColor.disable;
};

const SelectIconComponent: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  if (isLoading) {
    return <CircularProgress size={14} color="inherit" style={iconStyle} />;
  }
  return <IconExpandMore fontSize={16} color={text.secondary} style={iconStyle} />;
};

type TProcedureStatusSelectProps = {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
};

export const ProcedureStatusSelect: FC<TProcedureStatusSelectProps> = ({
  value,
  options,
  onChange,
  isLoading,
  disabled,
}) => {
  const bg = getStatusBg(value);
  const currentValueLabel = options.find((o) => o.value === value)?.label || value;

  if (disabled) {
    return <StyledDisabledChip label={currentValueLabel} bg={bg} />;
  }

  return (
    <StyledStatusSelect
      value={value}
      onChange={onChange}
      options={options}
      nonControl
      isShowIconComponent={false}
      statusBg={bg}
      disabled={isLoading}
      IconComponent={SelectIconComponent}
      MenuProps={{
        PaperProps: {
          sx: {
            width: MENU_WIDTH,
            mt: 0.5,
            "& .MuiMenuItem-root": {
              ...typography.body14,
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.4,
              display: "block",
            },
          },
        },
      }}
    />
  );
};

const StyledStatusSelect = styled(Select, {
  shouldForwardProp: (p) => p !== "statusBg",
})<{ statusBg: string }>`
  width: ${SELECT_WIDTH}px;
  max-width: ${SELECT_WIDTH}px;
  min-width: ${SELECT_WIDTH}px;
  .MuiInputBase-root,
  &.MuiInputBase-root {
    border-radius: 16px !important;
    background: ${({ statusBg }) => statusBg};
  }
  fieldset {
    border: none !important;
  }
  .MuiInputBase-root.Mui-focused fieldset,
  &.Mui-focused fieldset {
    border: none !important;
  }
  .MuiInputBase-root.Mui-focused::after,
  &.Mui-focused::after {
    content: none !important;
    display: none !important;
  }
  .MuiSelect-select {
    ${css(typography.body14)};
    padding: 4px 32px 4px 14px !important;
    color: ${text.primary} !important;
    background: transparent;
    border-radius: 16px !important;
    text-align: center;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    word-break: break-word;
    line-height: 1.4 !important;
    min-height: 0 !important;
  }
  .MuiSelect-icon {
    transform: none !important;
    right: 8px !important;
    top: 50%;
    margin-top: -8px;
  }
`;

const StyledDisabledChip = styled(Chip, {
  shouldForwardProp: (p) => p !== "bg",
})<{ bg: string }>`
  width: ${SELECT_WIDTH}px;
  max-width: ${SELECT_WIDTH}px;
  height: auto;
  background-color: ${({ bg }) => bg};
  color: ${text.primary};
  border-radius: 16px;
  & .MuiChip-label {
    ${css(typography.body14)};
    display: block;
    width: 100%;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
  }
`;
